import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { Database } from '../lib/supabase'

type Profile = Database['public']['Tables']['profiles']['Row']
type ProfileInsert = Database['public']['Tables']['profiles']['Insert']
type ProfileUpdate = Database['public']['Tables']['profiles']['Update']

const PROFILES_QUERY_KEY = ['profiles'] as const

// Query functions
const fetchProfiles = async (): Promise<Profile[]> => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
  return data || []
  }

const createProfile = async (profile: ProfileInsert): Promise<Profile> => {
      const { data, error } = await supabase
        .from('profiles')
        .insert(profile)
        .select()
        .single()

      if (error) throw error
  return data
  }

const updateProfile = async ({ id, updates }: { id: string; updates: ProfileUpdate }): Promise<Profile> => {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
  return data
  }

const deleteProfile = async (id: string): Promise<void> => {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', id)

      if (error) throw error
}

// Hooks
export const useProfiles = () => {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: PROFILES_QUERY_KEY,
    queryFn: fetchProfiles,
  })

  const createMutation = useMutation({
    mutationFn: createProfile,
    onSuccess: (newProfile) => {
      queryClient.setQueryData(PROFILES_QUERY_KEY, (old: Profile[] = []) => [newProfile, ...old])
    },
  })

  const updateMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(PROFILES_QUERY_KEY, (old: Profile[] = []) =>
        old.map(profile => profile.id === updatedProfile.id ? updatedProfile : profile)
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteProfile,
    onSuccess: (_, deletedId) => {
      queryClient.setQueryData(PROFILES_QUERY_KEY, (old: Profile[] = []) =>
        old.filter(profile => profile.id !== deletedId)
      )
    },
  })

  return {
    profiles: query.data || [],
    loading: query.isLoading,
    error: query.error?.message || null,
    refetch: query.refetch,
    createProfile: createMutation.mutateAsync,
    updateProfile: (id: string, updates: ProfileUpdate) => updateMutation.mutateAsync({ id, updates }),
    deleteProfile: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
